import { useEffect, useState } from 'react';
import {
  onSnapshot,
  query,
  orderBy,
  where,
  type Query,
  type DocumentData,
} from 'firebase/firestore';
import type { WbsConstraintsDoc, WbsItemDoc } from '@mct/shared';
import { getDb } from '../lib/firebase';
import { wbsConstraintsRef, wbsItemsCol } from '../lib/wbs';
import { useAuthState } from './useAuth';

export function useWbsItems(projectId: string | null) {
  const { user } = useAuthState();
  const uid = user?.uid ?? null;
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId || !uid) return setQ(null);
    // Firestore rules evaluate `isMember(resource.data)` against each doc, so
    // the query itself must be scoped to docs the caller can read, otherwise
    // the whole query is rejected. See design-addendum.md §2.
    setQ(
      query(
        wbsItemsCol(getDb(), projectId),
        where('memberUserIds', 'array-contains', uid),
        orderBy('sortKey', 'asc'),
      ),
    );
  }, [projectId, uid]);

  const [data, setData] = useState<WbsItemDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!q) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => d.data() as WbsItemDoc));
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [q]);

  return { data, loading, error };
}

export function useWbsConstraints(projectId: string | null) {
  const [data, setData] = useState<WbsConstraintsDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId) {
      setData(null);
      setLoading(false);
      return;
    }
    const ref = wbsConstraintsRef(getDb(), projectId);
    setLoading(true);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setData(snap.exists() ? (snap.data() as WbsConstraintsDoc) : null);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId]);

  return { data, loading, error };
}
